/* ═══════════════════════════════════════════════════════════════
   Org Chart V2 — legend ROW rendering, shared by the on-canvas
   `LegendOverlay` (view mode) and the old on-demand popover chrome.
   Pure content: computes entries via `legend.ts` (single source of
   truth, reads the SAME PersonNodeData every card renders from) and
   draws each entry with the REAL badge component a card uses
   (`LeaderBadge`) rather than a hand-drawn stand-in — the hand-drawn
   duplicate is how the leader entry drifted from what cards actually
   show (Josh bug report 2026-08-19).
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import type { Node as RFNode } from '@xyflow/react';
import { computeChartLegend, isChartLegendEmpty } from './legend';
import { LeaderBadge } from './nodes';
import { rem } from '../../../lib/rem';

const chip: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: rem(20),
  height: rem(18),
  padding: '0 ' + rem(5),
  fontSize: 'var(--font-size-xs)',
  fontWeight: 800,
  borderRadius: 'var(--radius-sm)',
  lineHeight: 1,
  flexShrink: 0,
};
const row: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--space-2)',
  padding: rem(3) + ' ' + rem(2),
  fontSize: 'calc(var(--font-size-base) * 12 / 13)',
  color: 'var(--tx)',
};

export function LegendContent({ nodes }: { nodes: RFNode[] }) {
  const legend = computeChartLegend(nodes);
  const empty = isChartLegendEmpty(legend);

  if (empty) {
    return (
      <div
        style={{
          fontSize: 'calc(var(--font-size-base) * 12 / 13)',
          color: 'var(--tm)',
          padding: 'var(--space-1) ' + rem(2),
        }}
      >
        No badges are currently shown on this board.
      </div>
    );
  }

  return (
    <>
      {legend.leader && (
        <div style={row}>
          {/* Same LeaderBadge component every card renders — a real card
              tints this per-team, so the legend uses a neutral tone (the
              per-team color is already decoded by the color dots
              elsewhere on the chart); shape/glyph is what this entry
              exists to explain. */}
          <LeaderBadge color="var(--tm)" />
          <span>{legend.leader.label}</span>
        </div>
      )}
      {legend.titleBadges.map((b) => (
        <div key={b.key} style={row}>
          <span
            style={{
              ...chip,
              background: 'var(--sf)',
              color: b.color,
              border: `1.5px solid ${b.color}`,
            }}
          >
            {b.code}
          </span>
          <span>{b.label}</span>
        </div>
      ))}
      {legend.departmentPills.map((p) => (
        <div key={p.key} style={row}>
          <span
            style={{
              ...chip,
              background: p.color + '18',
              color: p.color,
              border: `1px solid ${p.color}40`,
            }}
          >
            {p.code}
          </span>
          <span>{p.label}</span>
        </div>
      ))}
      {legend.inactive && (
        <div style={row}>
          <span style={{ ...chip, opacity: 0.5, background: 'var(--bd)' }} />
          <span>{legend.inactive.label}</span>
        </div>
      )}
    </>
  );
}
